import type { Dictionary } from "@/types/i18n";
import type { PaymentTierId } from "@/types/payments";

export type Tier = Dictionary["payment"]["tiers"][number];
export type PaymentCopy = Dictionary["payment"];

export const BUNDLE_TIER_ID: PaymentTierId = "career-os";

/**
 * The plan that earns the badge and is pre-selected on first render.
 *
 * Set to "career-os" so the highest-revenue bundle is highlighted by default
 * and counts of payers moving from 30k → 79k outweigh any badge-only test.
 * Change this constant (not the badge logic) to run an A/B experiment once
 * PostHog feature flags are wired (item 24).
 */
export const RECOMMENDED_TIER_ID: PaymentTierId = "career-os";

export const DISABLED_PAYMENT_TIER_IDS: PaymentTierId[] = [];

/**
 * A checkout option as the picker renders it. The CV review tiers come from
 * `dictionary.payment.tiers`; the Career Booster bundle is assembled from
 * `dictionary.payment.bundle` because it checks out through a different
 * endpoint, so both shapes are normalised here into one renderable plan.
 */
export interface PaymentPlan {
  id: PaymentTierId;
  name: string;
  description: string;
  price: number;
  /** Emphasised entitlements listed above `features` (bundle only). */
  highlights: string[];
  features: string[];
  /** Struck-through reference price, e.g. the bundle's undiscounted value. */
  anchorPrice: string | null;
  saveLabel: string | null;
  badge: string | null;
}

const idrFormatter = new Intl.NumberFormat("id-ID");

export function formatPrice(value: number) {
  return `Rp.${idrFormatter.format(value)}`;
}

export function replacePrice(template: string, price: number) {
  return template.replace("{{price}}", formatPrice(price));
}

export function replaceAmount(template: string, amount: number) {
  return template.replace("{{amount}}", formatPrice(amount));
}

export function replacePercent(template: string, percent: number) {
  return template.replace("{{percent}}", String(percent));
}
